'use client'
import React, { useEffect, useState } from 'react';
import Button from '@/components/CommonComponent/button';
import { IoCheckmark } from 'react-icons/io5';
import { RxCross2 } from 'react-icons/rx';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMinus, FaPlus } from 'react-icons/fa';

interface PackageProps {
  title: string;
  card: {
    title: string;
    basic_type: string;
    advance_type: string;
    professional_type: string;
    premium_type: string;
  }[];
}

interface PackagesComponentProps {
  props: PackageProps[];
  headtitle?: string;
  desc?: any;

}

const Packages: React.FC<PackagesComponentProps> = ({ props , headtitle, desc }) => {
  // console.log("props",props);
  
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
  const [packageCards, setPackageCards] = useState<PackageProps[]>([]);
// console.log(packageCards);

  const toggleExpand = (index: number) => {
    setExpandedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const renderCell = (value: string, planType: string) => {
    const lowerValue = value?.toLowerCase();
    const cellClasses = `flex flex-1 items-center justify-center p-2 font-medium`;
    if (lowerValue === 'true') {
      return <div className={cellClasses}><IoCheckmark className="text-[24px] text-blue" /></div>;
    } else if (lowerValue === 'false') {
      return <div className={cellClasses}><RxCross2 className="text-[24px] font-semibold text-homegrey" /></div>;
    }
    return (
      <div className={cellClasses}>
        {value || (
          <Link href={'/get-a-free-quote'}>
            <Button content="Get a quote" />
          </Link>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (props?.length > 0) {
      setPackageCards(props.slice(1));
    }
  }, [props]);

  return (
    <div className="py-10 lg:py-16 text-center">

      <div className="bg-white w-full px-4 xl:w-[95%] 2xl:w-[80%] mx-auto ">
        <div className="space-y-6">
        <h2>{headtitle}</h2>
        <p dangerouslySetInnerHTML={{__html:desc}}></p>
          <div className="shadow-lg p-5 border-slate-200 border-[1px] rounded-2xl mb-4">
            <div className="overflow-x-auto">
              <div className="flex justify-start mb-14">
                <div className="min-w-[230px] sm:min-w-[25%] min-h-full flex items-center">
                  <p className="text-[24px] font-semibold">{props[0]?.title || ''}</p>
                </div>
                <div className="w-full min-w-[800px] flex justify-around">
                  {['Basic', 'Advanced', 'Professional', 'Premium'].map((plan, index) => (
                    <div key={index} className="flex flex-col items-center">
                      <div className="w-fit">
                        <Button content={plan} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <table className="w-full min-w-[900px]">
                <tbody className="text-[18px] font-medium flex flex-col gap-3 text-textGrey">
                  {props[0]?.card?.map((item:any, cardIndex:any) => (
                    <tr className="border-b-2 flex border-slate-200" key={cardIndex}>
                      <td className="w-[30%] p-2">{item?.title}</td>
                      <div className="w-full flex justify-around">
                        <td className="flex flex-1 items-center justify-center p-2 font-medium">
                          {renderCell(item.basic_type, "Basic")}
                        </td>
                        <td className="flex flex-1 items-center p-2 justify-center font-medium">
                          {renderCell(item.advance_type, "Advanced")}
                        </td>
                        <td className="flex flex-1 items-center p-2 justify-center font-medium">
                          {renderCell(item.professional_type, "Professional")}
                        </td>
                        <td className="flex flex-1 items-center p-2 justify-center font-medium">
                          {renderCell(item.premium_type, "Premium")}
                        </td>
                      </div>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="w-full">
            {packageCards?.map((elem, index) => (
              <motion.div
                key={elem.title}
                className="mb-4 rounded-2xl border-[1px] border-slate-200 p-2 shadow-lg"
                initial={false}
                transition={{ duration: 0.3 }}
              >
                <div
                  className="flex cursor-pointer items-center gap-3 p-4"
                  onClick={() => toggleExpand(index)}
                >
                  <p className="text-[20px] font-semibold">{elem.title}</p>
                  <motion.div
                    animate={{ rotate: expandedIndices.includes(index) ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {expandedIndices.includes(index) ? (
                      <FaMinus className="rounded-md bg-grey p-2 text-[36px]" />
                    ) : (
                      <FaPlus className="rounded-md bg-grey p-2 text-[36px]" />
                    )}
                  </motion.div>
                </div>
                <AnimatePresence initial={false}>
                  {expandedIndices.includes(index) && (
                    <motion.div
                      key="content"
                      initial="collapsed"
                      animate="open"
                      exit="collapsed"
                      variants={{
                        open: { opacity: 1, height: "auto" },
                        collapsed: { opacity: 0, height: 0 }
                      }}
                      transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className="overflow-x-auto p-4"
                      >
                        <table className="w-full min-w-[900px]">
                          <tbody className="flex flex-col gap-3 text-[18px] text-textGrey">
                            {elem?.card?.map((item, cardIndex) => (
                              <tr key={cardIndex} className="flex border-b-2 border-slate-200">
                                <td className="w-[30%] p-2 font-medium">{item.title}</td>
                                <td className="flex flex-1 items-center justify-center p-2 font-medium">
                                  {renderCell(item.basic_type, "Basic")}
                                </td>
                                <td className="flex flex-1 items-center justify-center p-2 font-medium">
                                  {renderCell(item.advance_type, "Advanced")}
                                </td>
                                <td className="flex flex-1 items-center justify-center p-2 font-medium">
                                  {renderCell(item.professional_type, "Professional")}
                                </td>
                                <td className="flex flex-1 items-center justify-center p-2 font-medium">
                                  {renderCell(item.premium_type, "Premium")}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Packages;
